import React from "react";

import {
  buildFuelControlExampleMatrix,
  buildFuelControlMatrix,
} from "./fuelSupplyMetrics";

import "./FieldAppFuelControlMatrix.css";

function MatrixBlock({ matrix, t, title, meta, isExample }) {
  if (!matrix) return null;

  const { columns, rows } = matrix;

  return (
    <div
      className={`field-app-fuel-matrix-block${
        isExample ? " field-app-fuel-matrix-block--example" : ""
      }`}
    >
      {title ? (
        <div className="field-app-fuel-matrix-head">
          <strong>{title}</strong>
          {isExample ? (
            <span className="field-app-fuel-matrix-demo-badge">
              {t("Exemplo ilustrativo")}
            </span>
          ) : null}
          {meta ? <span className="field-app-fuel-matrix-meta">{meta}</span> : null}
        </div>
      ) : null}
      <div className="field-app-fuel-matrix-wrap">
        <table className="field-app-fuel-matrix">
          <thead>
            <tr>
              <th scope="col">{t("Indicador")}</th>
              {columns.map((column) => (
                <th key={column.id} scope="col">
                  {t(column.labelKey)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.id}>
                <th scope="row">{t(row.labelKey)}</th>
                {columns.map((column) => {
                  const bucket = column.bucket;
                  const value = row.pick(bucket);
                  const unreliable = bucket.unreliableAverage;
                  return (
                    <td
                      key={`${row.id}-${column.id}`}
                      className={
                        unreliable ? "field-app-fuel-matrix-cell--warn" : ""
                      }
                    >
                      <span className="field-app-fuel-matrix-value">
                        {row.format(value)}
                      </span>
                      {unreliable && row.id === "liters" ? (
                        <span
                          className="field-app-fuel-matrix-note"
                          title={t(
                            "Intervalo com abastecimento parcial — média estimada"
                          )}
                        >
                          {t("Parcial")}
                        </span>
                      ) : null}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function FieldAppFuelControlMatrix({
  summary,
  machineSummaries,
  recordCount = 0,
  t,
}) {
  const overallMatrix = buildFuelControlMatrix(summary);
  const hasIntervalData =
    overallMatrix || (machineSummaries && machineSummaries.length > 0);
  const hasRecords = recordCount > 0;

  if (!hasIntervalData && !hasRecords) {
    return (
      <div className="field-app-fuel-matrix">
        <p className="field-app-fuel-matrix-empty">
          {t(
            "Ainda não há abastecimentos no período filtrado. Se você abasteceu no tablet, amplie as datas ou aguarde a sincronização. A matriz abaixo é ilustrativa."
          )}
        </p>
        <MatrixBlock
          matrix={buildFuelControlExampleMatrix()}
          title={t("Máquina 100.005 (exemplo)")}
          meta={t("3 abast. · 2 intervalos")}
          isExample
          t={t}
        />
      </div>
    );
  }

  if (!hasIntervalData && hasRecords) {
    return (
      <div className="field-app-fuel-matrix">
        <p className="field-app-fuel-matrix-empty">
          {t(
            "Há abastecimentos no período, mas ainda não há intervalo fechado (tanque cheio) com horímetro ou hectares painel para calcular médias. Informe tanque cheio e leituras no próximo abastecimento."
          )}
        </p>
      </div>
    );
  }

  return (
    <div className="field-app-fuel-matrix">
      {machineSummaries && machineSummaries.length > 1 ? (
        machineSummaries.map((machine) => (
          <MatrixBlock
            key={machine.machineKey}
            matrix={buildFuelControlMatrix(machine.summary)}
            title={machine.machineLabel}
            meta={`${machine.recordCount} ${t("abast.")} · ${machine.summary.intervalCount} ${t("intervalos")}`}
            t={t}
          />
        ))
      ) : (
        <MatrixBlock
          matrix={overallMatrix}
          title={
            machineSummaries && machineSummaries.length === 1
              ? machineSummaries[0].machineLabel
              : t("Médias por intervalo")
          }
          meta={
            summary && summary.intervalCount
              ? `${summary.intervalCount} ${t("intervalos")}`
              : null
          }
          t={t}
        />
      )}
    </div>
  );
}
